import React, {
  ComponentType,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import * as RadixTab from "@radix-ui/react-tabs";
import styles from "./Tabs.module.scss";
import { IconArrowLeft, IconArrowRight, IconProps } from "../icons";
import { useWindowSize } from "../../helpers/window-size/windowsize";
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from "../Tooltip";

// Interface defining the structure of a Tab
export interface TabConfig {
  name: string;
  content: React.ReactNode;
  disabled?: boolean;
  Icon?: ComponentType<IconProps>;
}

// Interface defining the properties expected by TabsComponent
export interface TabsComponentProps {
  children?: React.ReactNode;
  defaultTab?: string;
  onTabChange?: (value: string) => void;
}

const CONFIG = {
  MAX_CHAR_LEN: 20,
};

// Easing function for smooth scrolling
const outQuint = (
  x: number,
  t: number,
  b: number,
  c: number,
  d: number,
): number => {
  return c * ((t = t / d - 1) * t * t * t * t + 1) + b;
};

export const TabsList = ({
  defaultTab,
  children,
  onTabChange,
}: TabsComponentProps) => {
  // Refs and states for handling tab overflow and scrolling
  const tabsRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [disableRightArrow, setDisableRightArrow] = useState(false);
  const tabRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [scrollInterval, setScrollInterval] = useState<ReturnType<
    typeof setInterval
  > | null>(null);
  const windowSize = useWindowSize();
  const [isMobile, setIsMobile] = useState(windowSize.width <= 768);
  const [tabWidth, setTabWidth] = useState(100);

  const tabs: TabConfig[] = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.type === Tab) {
      const { label: name, disabled, icon: Icon } = child.props;
      return { name, content: child.props.children, disabled, Icon };
    }
    return null;
  }) as TabConfig[];

  useEffect(() => {
    setIsMobile(windowSize.width <= 768);
    if (tabs.length > 0 && windowSize.width <= 768) {
      const fitNumberTabs = Math.min(3.5, tabs.length);
      const percentageToSubtract =
        tabs.length >= 4 ? (35 / windowSize.width) * 100 : 0;
      const calculatedWidth = Math.floor(
        (100 - percentageToSubtract) / fitNumberTabs,
      );
      setTabWidth(calculatedWidth);
    }
  }, [windowSize]);

  // Handle scroll event to update the scroll position and determine if the end is reached
  useEffect(() => {
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      setScrollPosition(target.scrollLeft);
      if (target.scrollLeft + target.clientWidth >= target.scrollWidth) {
        setDisableRightArrow(true);
      } else {
        setDisableRightArrow(false);
      }
    };
    const tabsCurrentRef = tabsRef.current;

    if (tabsCurrentRef) {
      tabsCurrentRef.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (tabsCurrentRef) {
        tabsCurrentRef.removeEventListener("scroll", handleScroll);
      }
    };
  }, []);

  // Update tabs overflow state on resize
  useEffect(() => {
    // Check if tabs overflow the container
    const checkForOverflow = () => {
      if (tabsRef.current) {
        if (isMobile) {
          // As per design we are showing 3 and the 4th tab half when on the mobile screen and has more than 3 tabs
          setIsOverflowing(tabs.length > 3.5);
        } else {
          setIsOverflowing(
            tabsRef.current.scrollWidth > tabsRef.current.clientWidth,
          );
        }
      }
    };
    checkForOverflow();
    window.addEventListener("resize", checkForOverflow);

    return () => {
      window.removeEventListener("resize", checkForOverflow);
    };
  }, []);

  // Function for smooth scrolling of tabs
  const smoothScrollAmount = useCallback(
    async (direction: "left" | "right", amount: number): Promise<void> => {
      return new Promise((resolve) => {
        if (!tabsRef.current) return;

        const current = tabsRef.current;
        const startTime = Date.now();
        const initialScroll = current.scrollLeft;
        let targetScroll: number;

        if (direction === "right") {
          targetScroll = initialScroll + amount;
        } else {
          targetScroll = initialScroll - amount;
        }
        const scroll = (): void => {
          const elapsed = Date.now() - startTime;
          const percentage = Math.min(1, elapsed / 150);

          const position = outQuint(
            0,
            percentage,
            initialScroll,
            targetScroll - initialScroll,
            1,
          );
          current.scrollLeft = position;

          if (percentage < 1) {
            requestAnimationFrame(scroll);
          } else {
            resolve();
          }
        };

        requestAnimationFrame(scroll);
      });
    },
    [],
  );

  // Handlers for continuous scrolling on mouse hold
  const handleMouseDown = useCallback(
    (direction: "left" | "right"): void => {
      const interval = setInterval(() => {
        smoothScrollAmount(direction, 10);
      }, 50);

      setScrollInterval(interval);
    },
    [smoothScrollAmount],
  );

  const handleMouseUp = useCallback((): void => {
    if (scrollInterval) {
      clearInterval(scrollInterval);
      setScrollInterval(null);
    }
  }, [scrollInterval]);

  // Cleanup interval on component unmount
  useEffect(() => {
    return () => {
      if (scrollInterval) {
        clearInterval(scrollInterval);
      }
    };
  }, [scrollInterval]);

  // Handle no tabs scenario
  if (tabs.length === 0) {
    return <div>No tabs available.</div>;
  }

  // Validation check for default tab
  const isValidDefaultTab = tabs.some((tab) => tab.name === defaultTab);

  return (
    <RadixTab.Root
      className={styles["TabsRoot"]}
      defaultValue={isValidDefaultTab ? defaultTab : tabs[0]?.name}
      activationMode={"manual"}
      onValueChange={onTabChange}
    >
      {/* Tab scrolling and navigation UI */}
      <div className={styles["tabsWrapper"]}>
        <div
          className={styles.tabsContainer}
          data-testid="tabsContainer"
          ref={tabsRef}
        >
          {isOverflowing && scrollPosition > 0 && (
            <div className={`${styles.scrollButtonWrapperLeft}`}>
              <button
                onClick={() =>
                  smoothScrollAmount(
                    "left",
                    tabsRef.current?.clientWidth
                      ? Math.floor(
                          tabsRef.current?.clientWidth /
                            Math.min(3.5, tabs.length),
                        )
                      : 25,
                  )
                }
                onMouseDown={() => handleMouseDown("left")}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                data-testid="leftArrow"
                aria-label="Left Arrow"
                className={`${styles.unsetButton} ${styles.scrollButton} ${styles.arrowEnabled}`}
              >
                <IconArrowLeft />
              </button>
            </div>
          )}
          <RadixTab.List
            className={styles["TabsList"]}
            style={{
              paddingRight: isOverflowing ? "24px" : "0",
              width: isMobile ? "100%" : "",
            }}
          >
            {/* Mapping and rendering individual tabs */}
            {tabs.map((tab, index) => (
              <div
                key={tab.name}
                ref={(el) => (tabRefs.current[index] = el)}
                className={styles["tabContainer"]}
                style={{ flex: isMobile ? `0 0 ${tabWidth}%` : "" }}
              >
                <TabsToolTip
                  text={tab.name}
                  condition={tab.name.length > CONFIG.MAX_CHAR_LEN}
                >
                  <RadixTab.Trigger
                    className={styles["TabsTrigger"]}
                    value={tab.name}
                    disabled={tab.disabled}
                    aria-disabled={tab.disabled}
                    aria-controls={tab.name.split(" ").join("")}
                    style={{ width: isMobile ? "100%" : "" }}
                    data-testid={tab.name}
                  >
                    {tab.Icon && <tab.Icon className={styles.icon} />}

                    {tab.name.length > CONFIG.MAX_CHAR_LEN
                      ? `${tab.name.substring(0, CONFIG.MAX_CHAR_LEN)}...`
                      : tab.name}
                  </RadixTab.Trigger>
                </TabsToolTip>
              </div>
            ))}
          </RadixTab.List>
          {isOverflowing && (
            <div className={`${styles.scrollButtonWrapperRight}`}>
              <button
                onClick={() =>
                  smoothScrollAmount(
                    "right",
                    tabsRef.current?.clientWidth
                      ? Math.floor(
                          tabsRef.current?.clientWidth /
                            Math.min(3.5, tabs.length),
                        )
                      : 25,
                  )
                }
                onMouseDown={() => handleMouseDown("right")}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                className={`${styles.unsetButton} ${styles.scrollButton} ${
                  disableRightArrow ? styles.arrowDisabled : styles.arrowEnabled
                }`}
                disabled={disableRightArrow}
                data-testid="rightArrow"
                aria-label="Right Arrow"
              >
                <IconArrowRight />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Tab content rendering */}
      {tabs.map((tab) => (
        <RadixTab.Content
          key={tab.name}
          className={styles["TabsContent"]}
          value={tab.name}
          id={tab.name.split(" ").join("")}
        >
          {tab.content}
        </RadixTab.Content>
      ))}
    </RadixTab.Root>
  );
};

export const Tab = ({
  label,
  children,
  icon: Icon,
  disabled,
}: {
  label: string;
  children: React.ReactNode;
  icon?: ComponentType<IconProps>;
  disabled?: boolean;
}) => {
  return (
    <RadixTab.Trigger
      className={styles["TabsTrigger"]}
      value={label}
      disabled={disabled}
      aria-disabled={disabled}
      aria-controls={label.split(" ").join("")}
      data-testid={label}
    >
      {Icon && <Icon className={styles.icon} />}
      {children}
    </RadixTab.Trigger>
  );
};
interface TabsToolTipProps {
  text: string;
  condition?: boolean;
}

const TabsToolTip = ({
  children,
  text,
  condition = true,
}: PropsWithChildren<TabsToolTipProps>) => {
  return condition ? (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipPortal>
        <TooltipContent data-testid="tab-tooltip-content" variant={"small"}>
          {text}
          <TooltipArrow className={styles.tooltipArrow} />
        </TooltipContent>
      </TooltipPortal>
    </Tooltip>
  ) : (
    <>{children}</>
  );
};
